import { useEffect } from 'react';
import { browserHistory } from 'react-router';

import useAsyncValue from '../../../../../hooks/useAsyncValue';
import useRequestConnectedToAsyncValue from '../../../../../hooks/useRequestConnectedToAsyncValue';
import { post } from '../../../../../redux/api';
import type { ProjectGroupRelease } from '../../../../../../shared/model-typings';
import type { CreateReleaseRequest } from '../../../../../../shared/releaseGroups';

const createRelease = (groupId: number | string, release: CreateReleaseRequest) => {
  return post<CreateReleaseRequest, ProjectGroupRelease>(`/api/project_group/${groupId}/release`, release);
};

type OnReleaseCreated = (release: ProjectGroupRelease) => Promise<void>;

/**
 * Hook which provides the function to create a release,
 * along with async state like isLoading and errors.
 */
const useCreateRelease = (onReleaseCreated: OnReleaseCreated) => {
  const asyncValue = useAsyncValue<ProjectGroupRelease>();
  //  TODO: looking to improve the typing for `request` in useRequestConnectedToAsyncValue so we don't need to do this,
  //  but at least it's typed correctly downstream.
  const onCreateRelease = useRequestConnectedToAsyncValue(createRelease, asyncValue) as unknown as typeof createRelease;

  //  After a new release group is created, we want to navigate to the releases page
  useEffect(() => {
    async function fetchReleaseAndNavigate() {
      //  Ensure that the release group was actually created before navigating.
      if (asyncValue.value?.projectGroupId) {
        await onReleaseCreated(asyncValue.value);
        browserHistory.push(`/projects/group/${asyncValue.value.projectGroupId}/releases`);
      }
    }
    fetchReleaseAndNavigate();
  }, [asyncValue.value, onReleaseCreated]);

  return {
    isCreatingRelease: asyncValue.isLoading,
    errors: asyncValue.errors,
    onResetErrors: asyncValue.empty,
    onCreateRelease,
  };
};

export default useCreateRelease;
